import React from 'react';
import { InlineText, TextLabel, Heading, TextBlock, UserInterfaceCheckCircleFill, Link, ExtendedTheme } from 'neon-design-system';
import { makeStyles, createStyles } from '@material-ui/styles';
import { DEFAULT_CHANNEL_COVER } from '../../../config';

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) =>
	createStyles({
		root: {
			display: 'flex',
			flexDirection: 'column',
			height: '100%',
			textDecoration: 'none',
			cursor: 'pointer',
			backgroundColor: theme.themeColors.blueGrey[800],
			padding: 0,
			borderRadius: theme.spacing(1),
			overflow: 'hidden',
			transition: 'all 200ms ease-out',
			[theme.breakpoints.up('md')]: {
				'&:hover': {
					backgroundColor: theme.themeColors.blueGrey[700],
					'& .channelCardImageOverlay': {
						opacity: 0.3
					},
					'& .channelCardTitle': {
						color: 'white'
					},
					'& .channelCardImage': {
						transform: 'translate(-2%, -2%) scale(1.05)'
					}
				}
			}
		},
		cardTop: {
			position: 'relative',
			height: theme.spacing(10),
			backgroundSize: 'cover',
			backgroundColor: theme.themeColors.blueGrey[700],
			overflow: 'hidden',
			[theme.breakpoints.up('lg')]: {
				height: theme.spacing(15)
			}
		},
		coverImage: {
			display: 'block',
			width: '100%',
			height: 'auto',
			zIndex: 0,
			transition: 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)'
		},
		premiumLabel: {
			position: 'absolute',
			top: theme.spacing(2),
			left: theme.spacing(2),
			zIndex: 2
		},
		isSubscribedLabel: {
			display: 'flex',
			gap: theme.spacing(1),
			position: 'absolute',
			alignItems: 'center',
			top: theme.spacing(2),
			right: theme.spacing(2),
			color: 'white',
			textTransform: 'uppercase',
			fontWeight: 'bold',
			zIndex: 2,
			textShadow: '1px 1px 8px rgba(0,0,0,0.8)'
		},
		imageOverlay: {
			display: 'block',
			position: 'absolute',
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
			backgroundColor: 'black',
			opacity: 0.5,
			transition: 'opacity 200ms ease-out',
			zIndex: 1
		},
		cardBottom: {
			display: 'flex',
			flexDirection: 'column',
			flex: '1',
			justifyContent: 'center',
			padding: theme.spacing(2)
		},
		channelName: {
			color: theme.themeColors.blueGrey[100],
			marginBottom: theme.spacing(0.5)
		},
		channelOwner: {
			color: theme.themeColors.blueGrey[300]
		}
	})
);

interface ChannelCardProps {
	name: string;
	owner: string;
	url: string;
	image?: string;
	premium?: boolean;
	isSubscribed?: boolean;
}

export const ChannelCard = ({ name, owner, url, premium, isSubscribed, image }: ChannelCardProps) => {
	const classes = useStyles();

	return (
		<Link className={[classes.root, 'channel-card'].join(' ')} to={url}>
			<div className={classes.cardTop}>
				<div className={[classes.imageOverlay, 'channelCardImageOverlay'].join(' ')}/>
				{premium && <TextLabel label="Premium" styleName="primary" className={classes.premiumLabel} />}
				{isSubscribed && <InlineText className={classes.isSubscribedLabel} styleName="caption2">Subscribed <UserInterfaceCheckCircleFill /></InlineText>}
				<img className={[classes.coverImage, 'channelCardImage'].join(' ')} src={image || DEFAULT_CHANNEL_COVER} alt={name} />
			</div>
			<div className={classes.cardBottom}>
				<Heading styleName="title2" xs="small" lg="medium" className={[classes.channelName, 'channelCardTitle'].join(' ')}>{name}</Heading>
				<TextBlock styleName="footnote" xs="small" lg="medium" className={classes.channelOwner}>Published by <strong>{owner}</strong></TextBlock>
			</div>
		</Link>
	);
};

export default ChannelCard;
